import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, MessageSquare, CalendarCheck } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const steps = [
  {
    icon: FileText,
    title: { fr: 'Décrivez votre projet', en: 'Describe Your Project' },
    desc: {
      fr: 'Remplissez notre formulaire avec vos dates, la taille de votre équipe et vos besoins.',
      en: 'Fill out our form with your dates, team size, and requirements.',
    },
  },
  {
    icon: MessageSquare,
    title: { fr: 'Recevez une proposition', en: 'Receive a Proposal' },
    desc: {
      fr: 'Notre équipe vous répond sous 24h avec une proposition détaillée.',
      en: 'Our team responds within 24 hours with a detailed proposal.',
    },
  },
  {
    icon: CalendarCheck,
    title: { fr: 'Validez', en: 'Confirm' },
    desc: {
      fr: '...on s\'occupe du reste.',
      en: '...we take care of the rest.',
    },
  },
];

const HowItWorksSection = () => {
  const { lang, t } = useLang();

  return (
    <section data-testid="how-it-works-section" className="py-24 md:py-32 bg-[#F5F1EC]">
      <div className="px-5 md:px-10 lg:px-14 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#888] mb-4">
            {t('howItWorks.label')}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#111] leading-tight">
            {t('howItWorks.title')}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-14">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="text-center"
                data-testid={`how-step-${i}`}
              >
                <div className="relative mx-auto mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#E92A3E]/8 flex items-center justify-center mx-auto">
                    <Icon className="text-[#E92A3E]" size={20} />
                  </div>
                  <span className="absolute -top-1 w-5 h-5 rounded-full bg-[#E92A3E] text-white text-[10px] font-semibold flex items-center justify-center"
                    style={{ right: 'calc(50% - 28px)' }}
                  >
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-base text-[#111] mb-2">
                  {step.title[lang]}
                </h3>
                <p className="text-sm text-[#444] leading-relaxed max-w-xs mx-auto">
                  {step.desc[lang]}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Link to="/inquiry" data-testid="how-it-works-cta" className="btn-primary rounded">
            {t('howItWorks.cta')}
          </Link>
          <p className="text-[11px] text-[#666] mt-4">
            {t('howItWorks.note')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
